import { useCurrentAccount, useGetBreeds } from '@application/api';
import {
  JoinBuyerFormController,
  LoginFormController,
  SeoController,
} from '@application/components';
import { PRO_PAGE_ROUTE_PER_PRO_TYPE } from '@application/constants';
import {
  formatApiPrice,
  translatedFieldWithFallback,
  useRouter,
  useShowToast,
} from '@application/utils';
import type { AnimalTypeEnum } from '@gql/graphql';
import { AccountCreatedFromEnum, ProfessionalTypeEnum, WorkerServicePriceType } from '@gql/graphql';
import type { PreBookingWarningData } from '@presentation/components';
import { EmptyOverlay, LoadingContainer } from '@presentation/components';
import type {
  BookingPageAuthMethod,
  BookingPageFormValues,
  BookingPageProData,
  BookingPageProps,
} from '@presentation/pages';
import { BookingPage } from '@presentation/pages';
import dayjs from 'dayjs';
import type { FormikConfig } from 'formik';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnimalInitialValuesBuilder } from '../MtAnimalBuyerPageController/api/useAnimalIntialValuesBuilder';
import { useValidateAnimalForm } from '../MtAnimalBuyerPageController/useValidateAnimalForm';
import { useCreateBooking } from './api/useCreateBooking';
import { useGetAnimals } from './api/useGetAnimals';
import { useGetPro } from './api/useGetPro';
import { useSearch } from './useSearch';
import { useSeo } from './useSeo';
import { useValidateBookingPageForm } from './useValidateBookingPageForm';

export const BookingPageController = () => {
  const [chosenAuth, onChosenAuthChange] = useState<BookingPageAuthMethod | undefined>();
  const [authFormIsDirty, setAuthFormIsDirty] = useState(false);
  const onAuthFormClick = useCallback(() => {
    setAuthFormIsDirty(true);
  }, []);

  const { showToast } = useShowToast();

  const { animalInitialValuesBuilder } = useAnimalInitialValuesBuilder();
  const { validateAnimalForm } = useValidateAnimalForm();
  const { currentAccountLoggedIn, currentAccountLoading, currentAccount, currentAccountType } =
    useCurrentAccount();

  const { navigateToRoute, translate, language, routeLocationDescriptor } = useRouter();

  const { createBookingFromForm } = useCreateBooking();
  const { breedsLoading, breedOptionsWithCrossbreedsFirst } = useGetBreeds();

  const { valuesFromSearch } = useSearch();

  const { animals, animalOptions, animalsLoading } = useGetAnimals(valuesFromSearch?.animalType);
  const { validateBookingPageForm } = useValidateBookingPageForm({
    forMultiAnimals: valuesFromSearch?.forMultiAnimals ?? false,
  });

  const { pro, proLoading } = useGetPro(valuesFromSearch?.proUrl);

  const { headerTitle, seoTitle } = useSeo(pro);

  const proService = (pro?.services || []).find((ps) =>
    (ps?.workerServices ?? []).some((ws) => ws.id === Number(valuesFromSearch?.workerServiceId)),
  );

  const workerService = proService?.workerServices.find(
    (ws) => ws.id === Number(valuesFromSearch?.workerServiceId),
  );
  const worker = (pro?.workers || []).find((w) => w.id === Number(valuesFromSearch?.workerId));
  const bookingStartValue = dayjs(valuesFromSearch?.bookingStart);

  const [currentStepState, setCurrentStep] = useState(0);

  const currentStep = useMemo(() => {
    if (currentAccountLoggedIn && currentStepState < 1) return 1;
    return currentStepState;
  }, [currentAccountLoggedIn, currentStepState]);

  useEffect(() => {
    if (currentStep > 0 && authFormIsDirty) {
      setAuthFormIsDirty(false);
    }
  }, [authFormIsDirty, currentStep]);

  const nextClickHandler = useCallback(() => {
    setCurrentStep(currentStep + 1);
  }, [currentStep, setCurrentStep]);

  const previousClickHandler = useCallback(() => {
    setCurrentStep(currentStep - 1);
  }, [currentStep, setCurrentStep]);

  const shouldRequirePhoneNumber = !currentAccount?.phone;

  useEffect(() => {
    setTimeout(() => {
      window?.scrollTo({ left: 0, top: 0, behavior: 'smooth' });
    }, 100);
  }, [currentStep]);

  if (bookingStartValue == null) {
    navigateToRoute('proLanding');
  }

  const formSubmitHandler: FormikConfig<BookingPageFormValues>['onSubmit'] = useCallback(
    async (values, formHelpers) => {
      if (!workerService || !worker) return;
      if (currentAccountLoggedIn && currentAccountType === 'breeder') {
        return showToast(translate('common.toasts.apolloError.booking/cannot-book-as-breeder'), {
          type: 'error',
        });
      }

      let error = false;

      try {
        await createBookingFromForm({
          ...values,
          workerServiceId: workerService.id,
          workerId: worker.id,
          accountAnimals: animals,
          forMultiAnimals: valuesFromSearch?.forMultiAnimals ?? false,
          onAnimalsMutation: (newAnimalIds) => {
            formHelpers.setFieldValue('animalIds', newAnimalIds);
          },
        });
      } catch (e) {
        error = true;
      }

      if (currentAccountLoggedIn && !error) {
        return navigateToRoute('mtAppointments');
      }

      if (pro?.type === ProfessionalTypeEnum.VET) {
        return navigateToRoute('vet', { params: [valuesFromSearch?.proUrl ?? ''] });
      }

      if (pro?.type === ProfessionalTypeEnum.GROOMER) {
        return navigateToRoute('groomer', { params: [valuesFromSearch?.proUrl ?? ''] });
      }
    },
    [
      workerService,
      worker,
      currentAccountLoggedIn,
      currentAccountType,
      createBookingFromForm,
      animals,
      valuesFromSearch?.forMultiAnimals,
      valuesFromSearch?.proUrl,
      pro?.type,
      showToast,
      translate,
      navigateToRoute,
    ],
  );

  const form = useFormik<BookingPageFormValues>({
    enableReinitialize: true,
    initialValues: {
      shouldRequirePhoneNumber,
      bookingStart: bookingStartValue,
      // eslint-disable-next-line no-nested-ternary
      animalIds: !animals?.length ? ['new'] : [],
      animalType: valuesFromSearch?.animalType,
      bookingReason: undefined,
      phoneNumber: undefined,
      animalBrushingFrequency: undefined,
      animalFirstTimeAtGroomer: undefined,
      animalHasKnottedFur: undefined,
      animalLastVisit: undefined,
      animalRecentlyAdopted: undefined,
      animalTemper: undefined,
      animals: [],
    },
    onSubmit: formSubmitHandler,
    validate: currentStep === 1 ? validateBookingPageForm : undefined,
  });

  const preBookingWarning = useMemo((): PreBookingWarningData => {
    return {
      proWarning:
        pro?.translations?.find((x) => x.language === language)?.preBookingWarning ?? undefined,
      workerServiceWarning:
        workerService?.translations?.find((x) => x.language === language)?.preBookingWarning ??
        undefined,
    };
  }, [language, pro?.translations, workerService?.translations]);

  const formattedPro = useMemo((): BookingPageProData | undefined => {
    if (!pro) return;
    let serviceDuration = workerService?.bookingDuration ?? undefined;
    if (valuesFromSearch?.forMultiAnimals && workerService?.multiAnimalsDuration) {
      serviceDuration = workerService?.multiAnimalsDuration;
    }
    return {
      id: pro?.id,
      type: pro?.type,
      title: headerTitle ?? undefined,
      service: translatedFieldWithFallback(language, proService?.service?.translations, 'title'),
      workerName: worker?.fullName ?? undefined,
      serviceDuration,
      image: pro?.imgs[0]?.small ?? undefined,
      address: `${pro?.address?.zip} ${pro?.address?.city}`,
      servicePrice: formatApiPrice({
        price: workerService?.price,
        priceFrom: workerService?.priceFrom,
        priceTo: workerService?.priceTo,
        fixedPrice: workerService?.priceType === WorkerServicePriceType.FIXED,
      }),
    };
  }, [
    headerTitle,
    language,
    pro,
    proService?.service?.translations,
    valuesFromSearch?.forMultiAnimals,
    worker?.fullName,
    workerService,
  ]);

  const animalFormComponentProps: BookingPageProps['animalFormComponentProps'] = useCallback(
    (selectedAnimalId) => {
      const selectedAnimal = (animals ?? []).find((a) => a.id === selectedAnimalId);
      return {
        disabledFields: {
          type: !!valuesFromSearch?.animalType,
          dogInfo: {
            breedId: !!valuesFromSearch?.breedId,
          },
        },
        mode: 'new',
        form: {
          onSubmit: () => undefined,
          validate: validateAnimalForm,
          initialValues: {
            ...(selectedAnimal
              ? animalInitialValuesBuilder(selectedAnimal)
              : {
                  ...animalInitialValuesBuilder(selectedAnimal),
                  type: valuesFromSearch?.animalType,
                  dogInfo: {
                    ...animalInitialValuesBuilder(selectedAnimal).dogInfo,
                    breedId: valuesFromSearch?.breedId,
                  },
                }),
          },
          options: {
            breeds: breedOptionsWithCrossbreedsFirst,
            animalTypes: (pro?.acceptedAnimals ?? []).map((animalTypeKey) => ({
              value: animalTypeKey as AnimalTypeEnum,
              label: translate(`enum.animalType.${animalTypeKey}`),
            })),
          },
        },
      };
    },
    [
      animalInitialValuesBuilder,
      animals,
      breedOptionsWithCrossbreedsFirst,
      pro?.acceptedAnimals,
      translate,
      validateAnimalForm,
      valuesFromSearch?.animalType,
      valuesFromSearch?.breedId,
    ],
  );

  const loading = useMemo(() => {
    return currentAccountLoading || proLoading;
  }, [currentAccountLoading, proLoading]);

  return (
    <FormikProvider value={form}>
      {loading && <LoadingContainer />}
      {!loading && !pro && <EmptyOverlay />}
      {!loading && !!pro && (
        <>
          <SeoController title={seoTitle} noIndex />
          <BookingPage
            header={{
              title: headerTitle,
              backLink: routeLocationDescriptor(PRO_PAGE_ROUTE_PER_PRO_TYPE[pro.type], {
                params: [valuesFromSearch?.proUrl ?? ''],
              }),
            }}
            forMultiAnimals={valuesFromSearch?.forMultiAnimals ?? false}
            chosenAuth={chosenAuth}
            onChosenAuthChange={onChosenAuthChange}
            currentStep={currentStep}
            onNextClick={nextClickHandler}
            onPreviousClick={previousClickHandler}
            accountAnimals={animals}
            loginForm={<LoginFormController />}
            joinBuyerForm={
              <JoinBuyerFormController
                createdFrom={AccountCreatedFromEnum.BOOKING}
                requireMarketingEmailConsent
              />
            }
            shouldRequirePhoneNumber={shouldRequirePhoneNumber}
            onEditInfoClick={() => setCurrentStep(1)}
            preBookingWarning={preBookingWarning}
            pro={formattedPro as BookingPageProData}
            animalsLoading={animalsLoading || breedsLoading}
            formOptions={{
              animalOptions,
            }}
            animalFormComponentProps={animalFormComponentProps}
            onAuthFormClick={onAuthFormClick}
          />
        </>
      )}
    </FormikProvider>
  );
};
